//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

type Listener = () => void;

const listenersByTeamspace = new Map<string, Set<Listener>>();

/**
 * Notify any subscribed poller that ingestion was just triggered for this
 * teamspace, so it can refresh immediately instead of waiting out its
 * current backoff interval. Deliberately teamspace-scoped, not tied to
 * whichever component made the trigger call — any mounted
 * useIngestJobsPolling instance for this teamspace reacts, regardless of
 * which host component (MCPTab, SidePanel, Integration) triggered it.
 */
export function notifyIngestionTriggered(teamspaceId: string): void {
  const normalized = (teamspaceId || '').trim();
  if (!normalized) return;
  listenersByTeamspace.get(normalized)?.forEach((listener) => listener());
}

/**
 * Subscribe to ingestion-triggered notifications for a teamspace. Returns
 * an unsubscribe function.
 */
export function subscribeIngestionTriggered(
  teamspaceId: string,
  listener: Listener,
): () => void {
  const normalized = (teamspaceId || '').trim();
  if (!normalized) return () => {};

  let listeners = listenersByTeamspace.get(normalized);
  if (!listeners) {
    listeners = new Set();
    listenersByTeamspace.set(normalized, listeners);
  }
  listeners.add(listener);

  return () => {
    listeners?.delete(listener);
    if (listeners && listeners.size === 0) {
      listenersByTeamspace.delete(normalized);
    }
  };
}
